"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Building2,
  Sparkles,
  Landmark,
  ShieldCheck,
  Scale,
  Eye,
  Vote,
  Lock,
  Activity,
  Target,
  Users,
  Globe2,
  Briefcase,
  Award,
  ArrowRight,
  Mic,
  Compass,
  BookOpen,
  Heart,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { ArchitectureDiagramBlock } from "@/lib/blocks/architecture-diagram-block";
import { CounterBlock } from "@/lib/blocks/counter-block";
import { FeatureGridBlock } from "@/lib/blocks/feature-grid-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const HEADLINE_STATS = [
  { metric: "34", suffix: "%→94%", label: "Policy Implementation", sub: "Verified transformation", accent: "red" },
  { metric: "42", suffix: "%→89%", label: "Citizen Satisfaction", sub: "Across deployments", accent: "purple" },
  { metric: "200", suffix: "+", label: "Government Deployments", sub: "Worldwide", accent: "cyan" },
  { metric: "900", suffix: "M+", label: "Citizens Served", sub: "Eighteen countries", accent: "emerald" },
  { metric: "95", suffix: "%", label: "Fraud Detection", sub: "True positive rate", accent: "amber-gold" },
  { metric: "127", suffix: "", label: "Governance Modules", sub: "Nine function categories", accent: "denim" },
];

const PILLARS = [
  { icon: Eye, title: "Perception, Reputation & Public Relations", body: "Citizen perception directly affects electoral mandate. Narrative management requires the precision and speed that only integrated architecture can deliver — communicating policy achievements, managing public sentiment, countering misinformation across every digital surface.", accent: "red" },
  { icon: Lock, title: "Privacy, Encryption & Information Security", body: "Sovereign communications must never be compromised. Government data — citizen records, policy deliberations, intelligence assessments — demands the same security infrastructure that protects national defense communications.", accent: "purple" },
  { icon: Vote, title: "Politics, Elections & Campaigning", body: "Electoral intelligence and voter understanding inform governance design. Ground-truth booth-level data reveals what citizens need, what they prioritize, and how they evaluate government performance — intelligence no digital-only analytics can replicate.", accent: "cyan" },
  { icon: ShieldCheck, title: "Policing, Intelligence & Defense", body: "National security depends on integrated intelligence across all law enforcement and defense agencies. Cross-agency fusion reduces intelligence-to-decision time from twenty-four to seventy-two hours to under sixty minutes.", accent: "emerald" },
  { icon: Scale, title: "Policy, Governance & Transformation", body: "The core governance pillar — converting policy intent into measurable citizen outcomes. The eGovernance operating system transforms electoral promises into verifiable delivery through automation, integration, and accountability at every level.", accent: "amber-gold" },
];

const CHALLENGES = [
  { num: "01", title: "Policy Implementation Failure", body: "Only a fraction of policies ever reach full implementation before engagement. The remaining public funds generate zero measurable impact. Every policy that fails to implement is a broken promise to citizens — and broken promises erode the mandate to govern.", accent: "red" },
  { num: "02", title: "Citizen Satisfaction Deficit", body: "When less than half the population trusts government to deliver, every reform becomes an uphill battle against institutional cynicism. The gap between what governments commit to and what citizens actually receive is the governance gap that defines the sector.", accent: "purple" },
  { num: "03", title: "Bureaucratic Friction", body: "The multi-year average delay in policy implementation caused by approval cycles measured in months, redundant processes consuming staff time, and accountability gaps allowing poor performance to persist undetected.", accent: "cyan" },
  { num: "04", title: "Digital Fragmentation", body: "Governments that have attempted digitization find themselves with disconnected systems creating new silos. Hundreds of discrete software systems that cannot communicate, producing data silos that make cross-ministerial coordination impossible.", accent: "emerald" },
  { num: "05", title: "Security Exposure", body: "Sovereign communications handled through channels that external entities can monitor, intercept, or compromise. In an era where nation-state cyber threats target government infrastructure daily, the absence of sovereign security architecture is not a gap — it is a vulnerability.", accent: "amber-gold" },
  { num: "06", title: "Integration at Sovereign Scale", body: "National governments operating across dozens of regional languages, processing millions of citizen interactions daily, coordinating hundreds of departments — this is not a technology problem. It is an orchestration problem demanding architecture designed for sovereign operations.", accent: "denim" },
];

const SERVICE_DOMAINS = [
  { icon: Target, title: "eGovernance & Digital Transformation", body: "End-to-end government process digitization from the cabinet room to the citizen&apos;s doorstep. Single-window service delivery with complete audit trails. This is not digitizing existing paper workflows — it is redesigning processes for digital efficiency.", accent: "red" },
  { icon: Activity, title: "Public Scheme Implementation Tracking", body: "Real-time monitoring from budget allocation through beneficiary verification to outcome measurement. The visibility required to ensure public funds reach intended purpose, with fraud detection achieving ninety-five percent true positive rates.", accent: "purple" },
  { icon: Users, title: "Citizen Engagement Platforms", body: "Multi-channel delivery across web portals, mobile applications, IVR voice interfaces, and physical service centers. Cross-agency coordination breaks departmental silos. Personalized service delivery adapts to citizen profile, history, and accessibility requirements.", accent: "cyan" },
  { icon: Scale, title: "Policy Impact Assessment", body: "AI-powered policy impact modeling that simulates thousands of permutations before implementation. Cross-jurisdictional comparison enables governments to learn from what has worked elsewhere. Predictive outcome forecasting across economic, social, and demographic dimensions.", accent: "emerald" },
  { icon: Briefcase, title: "Public Financial Management", body: "Real-time budget tracking, automated fraud detection, and comprehensive audit trail generation. The fraud detection engine operates across four modalities with ninety-five percent true positive detection and under five percent false positives.", accent: "amber-gold" },
  { icon: BookOpen, title: "Service Delivery Transformation", body: "End-to-end citizen journey mapping across all government services. Friction point identification using behavioral analytics. Proactive service notification anticipates citizen needs based on life events — automatically triggering relevant services.", accent: "denim" },
];

const OUTCOMES = [
  { metric: "34→94", suffix: "%", label: "Policy Implementation", sub: "Sixty percent more public funds reach purpose", accent: "red" },
  { metric: "42→89", suffix: "%", label: "Citizen Satisfaction", sub: "Trust restored at scale", accent: "purple" },
  { metric: "58", suffix: "%", label: "Cycle Reduction", sub: "Service delivery compressed", accent: "cyan" },
  { metric: "80", suffix: "%", label: "Report Time Cut", sub: "From five days to one", accent: "emerald" },
  { metric: "95", suffix: "%", label: "Fraud Detection", sub: "True positive rate", accent: "amber-gold" },
  { metric: "340", suffix: "%", label: "Engagement Increase", sub: "Citizen participation", accent: "denim" },
];

const CLIENTS = [
  { title: "Federal Ministries & National Agencies", body: "Comprehensive eGovernance implementation, policy intelligence, sovereign security infrastructure, and citizen engagement platforms across all tiers of administration. Engagements typically span multiple ministry departments and require coordination through the full five-pillar architecture.", accent: "red" },
  { title: "State & Provincial Governments", body: "Digital transformation, scheme implementation tracking, citizen service delivery modernization, and public financial management across regional administration. Often serve as proof-of-concept deployments that demonstrate measurable outcomes before national-scale expansion.", accent: "purple" },
  { title: "Local & Municipal Governments", body: "Citizen engagement platforms, service delivery transformation, urban planning systems, and administrative modernization. Demonstrate that governance transformation principles apply at every scale — from a single municipal authority to an entire national government.", accent: "cyan" },
  { title: "Sovereign Institutions", body: "The highest levels of discretion, security, and operational integrity — including executive offices, diplomatic corps, and multilateral bodies. Absolute non-disclosure, compartmentalized operations, and zero external dependency.", accent: "emerald" },
];

export default function GovernmentsPage() {
  return (
    <>
      <PageHero
        icon={Landmark}
        overline="Governments"
        overlineClassName="text-red"
        title={
          <>
            Strategic Sovereignty for <span className="text-gradient-premium">Governments.</span>
          </>
        }
        subtitle="Federal ministries, state departments, and sovereign institutions face a singular challenge: delivering governance outcomes at national scale while managing perception, protecting sovereignty, ensuring security, and maintaining citizen trust — simultaneously. Dr. Jyoti Kush provides the integrated operational architecture that makes this possible."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Sectors", href: "/sectors/" }, { label: "Governments" }]}
        badge="Governance transformation. Delivered."
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-5`}>
              <Sparkles className="size-3.5 text-purple" />
              <span className="text-overline text-purple">Executive Summary</span>
            </div>
            <h2 className="text-display-md mb-5">
              Governance transformation backed by sovereign-scale operations.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              Dr. Jyoti Kush serves governments and sovereign institutions that require unified command over perception, privacy, intelligence, and governance delivery. The engagement model draws from all five pillars of the Penta P Framework — Perception, Privacy, Politics, Policing, and Policy — converging to create total strategic sovereignty. This is not a consulting engagement. This is an integrated operational architecture deployed at national scale.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The government sector is one of only two sectors where all five pillars activate simultaneously — the other being political organizations. This full-spectrum activation reflects the reality that governments operate at the intersection of every strategic domain: public perception directly affects electoral mandate, policy decisions require intelligence-grounded formation, citizen services demand secure digital infrastructure, and national security depends on sovereign communications that no external entity can compromise.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              Government engagements have processed data for over nine hundred million citizens across eighteen countries. The verified outcomes include policy implementation rates rising from thirty-four to ninety-four percent, citizen satisfaction improving from forty-two to eighty-nine percent, and fraud detection achieving ninety-five percent true positive rates with under five percent false positives. These are not projections. These are operational results.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {HEADLINE_STATS.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${ACCENT[s.accent].bg} to-transparent border ${ACCENT[s.accent].border} hover:scale-[1.01] transition-all`}
              >
                <div className={`text-display-sm font-bold ${ACCENT[s.accent].text} leading-none mb-2`}>
                  {s.metric}{s.suffix}
                </div>
                <div className="text-caption font-bold uppercase tracking-wider mb-1">{s.label}</div>
                <div className="text-caption text-muted-foreground">{s.sub}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Globe2 className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Five Pillars in Full Activation</span>
          </div>
          <h2 className="text-display-md mb-4">Why government requires all five pillars.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Government is the only domain where all five strategic pillars operate at maximum intensity simultaneously. Perception determines mandate. Privacy protects sovereignty. Politics shapes policy. Policing ensures security. Policy delivers outcomes. Each pillar depends on the others.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PILLARS.map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center mb-4 shadow-lg`}>
                    <p.icon className="size-6 text-white" />
                  </div>
                  <h3 className={`text-heading-md mb-3 ${a.text}`}>{p.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{p.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <Activity className="size-3.5 text-red" />
            <span className="text-overline text-red">The Governance Challenge</span>
          </div>
          <h2 className="text-display-md mb-4">Six dimensions of the governance gap.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Governments face a paradox that no conventional consulting firm has resolved: unlimited authority but constrained capability, vast resources but bureaucratic machinery designed for a different era, promises at election speed but delivery at administrative crawl.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {CHALLENGES.map((c, i) => {
            const a = ACCENT[c.accent];
            return (
              <motion.div
                key={c.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl" />
                <div className="relative">
                  <div className={`text-display-sm font-bold ${a.text} opacity-30 leading-none mb-3`}>{c.num}</div>
                  <h3 className={`text-heading-md mb-3 ${a.text}`}>{c.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{c.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Briefcase className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Government Services Portfolio</span>
          </div>
          <h2 className="text-display-md mb-4">Six service domains across the governance lifecycle.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Government engagements deploy integrated services spanning the entire governance lifecycle — from policy formation through service delivery to citizen feedback. Battle-tested capabilities refined through deployments across eighteen countries and over two hundred government organizations.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {SERVICE_DOMAINS.map((s, i) => {
            const a = ACCENT[s.accent];
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="flex items-start gap-3.5">
                  <div className={`size-11 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <s.icon className="size-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{s.title}</h3>
                    <p className="text-body-sm text-muted-foreground leading-relaxed">{s.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Award className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">Verified Outcomes</span>
          </div>
          <h2 className="text-display-md mb-4">Transformation metrics from the field.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Every metric traces to operational data across two hundred government deployments. No claim is ungrounded. The evidence is not aspirational — it is the verified output of sovereign-scale operations.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {OUTCOMES.map((o, i) => {
            const a = ACCENT[o.accent];
            return (
              <motion.div
                key={o.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} text-center`}
              >
                <div className={`text-display-md font-bold ${a.text} leading-none mb-2`}>{o.metric}{o.suffix}</div>
                <div className="text-caption font-bold uppercase tracking-wider mb-1">{o.label}</div>
                <div className="text-caption text-muted-foreground">{o.sub}</div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Users className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Ideal Government Clients</span>
          </div>
          <h2 className="text-display-md mb-4">Who benefits from this engagement.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The common thread across all ideal clients: the recognition that governance transformation requires integrated systems, not siloed solutions. Every ideal client shares the understanding that only integrated architecture produces the compounding improvement that transforms governance from administrative burden into electoral advantage.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {CLIENTS.map((c, i) => {
            const a = ACCENT[c.accent];
            return (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <h3 className={`text-heading-md mb-3 ${a.text}`}>{c.title}</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed">{c.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <span className="text-overline text-amber-gold">Signature Architecture</span>
          </div>
          <h2 className="text-display-md mb-4">Government Service Map.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The integrated architecture that places citizen outcomes at the center and orbits every governance capability around delivery. Eight service domains coordinate as one system under unified command.
          </p>
        </div>
        <ArchitectureDiagramBlock
          eyebrow="Integrated Architecture"
          title="The Eight Domains Surrounding the Citizen"
          description="Every service domain orbits a single mission: verifiable delivery of policy intent into citizen outcomes. The architecture is the same that serves federal ministries, state departments, and sovereign institutions across eighteen countries."
          center={{
            label: "Citizen Outcomes",
            body: "Verified policy implementation, citizen satisfaction, and fraud-resistant service delivery at sovereign scale.",
            icon: <Award className="size-6" />,
            accent: "amber-gold",
          }}
          satellites={[
            { label: "eGovernance", body: "End-to-end process digitization across 127 modules.", icon: <Building2 className="size-4" />, accent: "red" },
            { label: "Scheme Tracking", body: "Real-time budget-to-outcome monitoring with 95% fraud detection.", icon: <Activity className="size-4" />, accent: "purple" },
            { label: "Citizen Engagement", body: "47 languages, multi-channel service delivery.", icon: <Users className="size-4" />, accent: "cyan" },
            { label: "Policy Modeling", body: "AI-powered outcome forecasting across thousands of permutations.", icon: <Target className="size-4" />, accent: "emerald" },
            { label: "Public Finance", body: "Real-time budget tracking, automated fraud detection.", icon: <Briefcase className="size-4" />, accent: "amber-gold" },
            { label: "Service Delivery", body: "Life-event triggered service notification, friction mapping.", icon: <BookOpen className="size-4" />, accent: "denim" },
            { label: "Sovereign Security", body: "Zero-trust communications, post-quantum encryption.", icon: <Lock className="size-4" />, accent: "red" },
            { label: "Intelligence Fusion", body: "200+ platforms, sub-60-second threat detection.", icon: <Eye className="size-4" />, accent: "purple" },
          ]}
          connections={[
            { from: -1, to: 0 },
            { from: -1, to: 1 },
            { from: -1, to: 2 },
            { from: -1, to: 3 },
            { from: -1, to: 4 },
            { from: -1, to: 5 },
            { from: -1, to: 6 },
            { from: -1, to: 7 },
          ]}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <span className="text-overline text-cyan">Deployment Evidence</span>
          </div>
          <h2 className="text-display-md mb-4">Eighteen countries. Two hundred deployments.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The metrics that follow are not projections. They trace to operational data across deployments where the margin for error is measured in national stability.
          </p>
        </div>
        <CounterBlock
          columns={3}
          stats={[
            { value: 9, suffix: "", label: "Function Categories", description: "Covered by 127 governance modules", accent: "cyan" },
            { value: 47, suffix: "", label: "Languages Supported", description: "Native IVR, sentiment, and document rendering", accent: "emerald" },
            { value: 95, suffix: "%", label: "Fraud True Positives", description: "Under 5% false positives across four modalities", accent: "red" },
            { value: 10000, suffix: "+", label: "Concurrent Requests/sec", description: "10,000+ citizen service requests sustained per second", accent: "amber-gold" },
            { value: 58, suffix: "%", label: "Cycle Reduction", description: "Service delivery compressed from 6 months to 2.5", accent: "purple" },
            { value: 100, suffix: "%", label: "Program Traceability", description: "Policy-to-impact tracked for 100% of programs", accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <span className="text-overline text-emerald">Vertical Specializations</span>
          </div>
          <h2 className="text-display-md mb-4">Eight governance domains. One operating system.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Beyond the horizontal services catalog, the platform delivers specialized transformation across eight vertical sectors — each configured for domain-specific requirements while maintaining integration with the broader government ecosystem.
          </p>
        </div>
        <FeatureGridBlock
          columns={4}
          features={[
            { title: "National ID & Digital Identity", body: "Four-tier verification from demographic matching to biometric authentication, interoperable with international standards.", icon: <ShieldCheck className="size-5" />, accent: "red" },
            { title: "Taxation & Revenue", body: "End-to-end tax administration digitization with AI-powered compliance verification and evasion detection.", icon: <Briefcase className="size-5" />, accent: "purple" },
            { title: "Customs & Trade", body: "Digital clearance automation reducing cargo release times from days to hours through paperless processing.", icon: <Globe2 className="size-5" />, accent: "cyan" },
            { title: "Judiciary & Legal", body: "Court case management from filing to enforcement, with hearing scheduling reducing case backlog.", icon: <Scale className="size-5" />, accent: "amber-gold" },
            { title: "Social Protection", body: "Eligibility determination, enrollment, benefit calculation, and disbursement unified across welfare programs.", icon: <Heart className="size-5" />, accent: "emerald" },
            { title: "Agriculture & Food", body: "Farm registration to market access with satellite-verified crop insurance automation.", icon: <Award className="size-5" />, accent: "denim" },
            { title: "Environment & Climate", body: "Real-time environmental monitoring through sensor networks and satellite imagery integration.", icon: <Compass className="size-5" />, accent: "lime" },
            { title: "Energy & Utilities", body: "Generation through transmission to consumption governance modernization at national scale.", icon: <Sparkles className="size-5" />, accent: "red" },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-red/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-amber-gold/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Sparkles className="size-10 text-red mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Engage Dr. Jyoti Kush.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Government engagements begin with intelligence, not assumptions. Six-stage methodology applied without modification. National security level confidentiality from first contact. The standard is sovereign. The approach is the same.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin an Engagement
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/sectors/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Compass className="size-4" />
                All Sectors
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Defense & Intelligence", description: "Sovereign-grade security and intelligence for defense agencies.", href: "/sectors/defense-intelligence/", icon: ShieldCheck, accent: "emerald" },
          { title: "Political Organizations", description: "Voter intelligence, campaign engineering, verified outcomes.", href: "/sectors/political-organizations/", icon: Vote, accent: "cyan" },
          { title: "Presidential & PM Offices", description: "Strategic command at the highest level.", href: "/sectors/presidential-pm-offices/", icon: Scale, accent: "amber-gold" },
          { title: "Sectors Hub", description: "Eight sectors. One operating system.", href: "/sectors/", icon: Building2, accent: "red" },
        ]}
      />
    </>
  );
}
